import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import PlatformIssuedLicenseExpandDialog from "@/components/platform/PlatformIssuedLicenseExpandDialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  formatRequestedModulesLabel,
  formatRequiresInvoiceLabel,
  invoiceStatusLabel,
  licenseRequestStatusLabel,
  moduleIdsForSaasCenter,
  moduleLabel,
} from "@/lib/commercialLicenseRequests";
import { DEFAULT_MODULE_CATALOG } from "@/modules/catalogModulos";
import {
  cfdiUseSelectLabel,
  paymentMethodSelectLabel,
} from "@/lib/commercialInvoiceCatalog";
import {
  getPlatformLicenseRequest,
  getPlatformLicenseRequestFulfillPreview,
  fulfillPlatformLicenseRequest,
  listPlatformLicenseRequests,
  updatePlatformLicenseRequestStatus,
  validatePlatformPaymentProof,
  fetchCommercialPlansCatalog,
  fetchPlatformModules,
} from "@/lib/platformApi";
import {
  billingCadenceLabel,
  commercialPlanLabel,
  commercialPlanModuleIds,
  commercialPlanOption,
} from "@/lib/commercialPlanUi";
import {
  canExpandIssuedPlatformLicenseRequest,
  canFulfillPlatformLicenseRequest,
  isPaymentProofPending,
  isPaymentProofRejected,
  parsePlatformLicenseRequestFulfillError,
  parsePlatformLicenseRequestListError,
  parsePlatformLicenseRequestPatchError,
  platformLicenseRequestActions,
  platformLicenseRequestStatusQuery,
  isFulfillResultComplete,
  PLATFORM_LICENSE_REQUEST_EMIT_PATH,
  PLATFORM_LICENSE_REQUEST_MSG_APPROVED_NEXT,
  PLATFORM_LICENSE_REQUEST_MSG_FULFILL_SUCCESS,
  PLATFORM_LICENSE_REQUEST_MSG_FULFILL_INCOMPLETE,
  PLATFORM_LICENSE_REQUEST_MSG_HEADER,
  PLATFORM_LICENSE_REQUEST_MSG_NO_AUTO_EMIT,
  PLATFORM_LICENSE_REQUEST_MSG_PATCH_SUCCESS,
  PLATFORM_LICENSE_REQUEST_MSG_PAYMENT_PENDING,
  PLATFORM_LICENSE_REQUEST_MSG_PAYMENT_REJECTED,
  PLATFORM_LICENSE_REQUEST_REVIEW_PLACEHOLDER,
  licenseRequestDisplayName,
  licenseRequestDisplayRfc,
  licenseRequestHasCommercialDrift,
  licenseRequestCurrentCommercialName,
  licenseRequestCurrentCommercialRfc,
  licenseRequestCommercialCodeLabel,
  licenseRequestCommercialCodeSummary,
  licenseRequestOriginalSummary,
} from "@/lib/platformLicenseRequestsUi";
import {
  LICENSE_TYPE_IDS,
  licenseTypeLabel,
  suggestedValidityDates,
  DEFAULT_ISSUED_LICENSE_TYPE,
} from "@/lib/licenseTypes";
import { LICENSE_SCOPE_COST_DISCLAIMER } from "@/lib/commercialLicenseScope";
import PlatformLicenseRequestDetailBody from "@/components/platform/PlatformLicenseRequestDetailBody";
import { paymentValidationStatusLabel, requestKindLabel, isExpansionRequestKind } from "@/lib/platformLicenseRequestDetailUi";

const STATUS_FILTERS = [
  { id: "all", label: "Todas" },
  { id: "pending", label: "Pendiente" },
  { id: "in_review", label: "En revisión" },
  { id: "approved", label: "Aprobada" },
  { id: "issued", label: "Licencia emitida" },
  { id: "rejected", label: "Rechazada" },
];

function defaultFulfillDates() {
  const from = new Date();
  const to = new Date(from);
  to.setFullYear(to.getFullYear() + 1);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { validFrom: fmt(from), validTo: fmt(to) };
}

function LicenseRequestCard({
  row,
  moduleCatalog,
  onOpenDetail,
  onOpenReview,
  onOpenFulfill,
  onOpenExpand,
}) {
  const actions = platformLicenseRequestActions(row.status);
  const showFulfill = canFulfillPlatformLicenseRequest(row);
  const showExpand = canExpandIssuedPlatformLicenseRequest(row);
  const isExpansion = isExpansionRequestKind(row.request_kind);

  return (
    <article
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm space-y-3"
      data-testid={`license-request-card-${row.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <h3 className="text-sm font-semibold text-[#0F2D5C] break-words">
            {licenseRequestCurrentCommercialName(row)}
          </h3>
          {licenseRequestCommercialCodeLabel(row) ? (
            <p className="text-xs font-mono text-[#0F2D5C]/80 break-all">
              {licenseRequestCommercialCodeLabel(row)}
            </p>
          ) : null}
          <p className="text-xs font-mono text-slate-600 break-all">
            RFC {licenseRequestCurrentCommercialRfc(row)}
          </p>
          {licenseRequestHasCommercialDrift(row) ? (
            <p
              className="text-[11px] text-slate-500 leading-snug"
              data-testid={`license-request-original-${row.id}`}
            >
              Solicitud original: {licenseRequestOriginalSummary(row)}
            </p>
          ) : null}
          <p className="text-xs text-slate-500">{formatDateShort(row.created_at)}</p>
          <p className="text-xs text-slate-600">{requestKindLabel(row.request_kind)}</p>
        </div>
        <div className="flex flex-wrap gap-1.5 justify-end">
          <span
            className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.status)}`}
          >
            {licenseRequestStatusLabel(row.status)}
          </span>
          {isExpansion ? (
            <Badge variant="outline" className="text-[10px] border-indigo-300 text-indigo-900 bg-indigo-50">
              Ampliación
            </Badge>
          ) : null}
          {row.commercial_review_required ? (
            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-900 bg-amber-50">
              Revisión comercial
            </Badge>
          ) : null}
          {row.special_quote_required ? (
            <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-900 bg-violet-50">
              Cotización especial
            </Badge>
          ) : null}
        </div>
      </div>

      <dl className="grid gap-2 text-xs sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="text-slate-500">Correo</dt>
          <dd className="text-slate-800 break-all">{row.email || "—"}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Tipo de licencia</dt>
          <dd className="text-slate-800">{licenseTypeLabel(row.requested_license_type) || "—"}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Plan solicitado</dt>
          <dd className="text-slate-800">
            {row.commercial_plan
              ? `${commercialPlanLabel(row.commercial_plan)} · ${billingCadenceLabel(row.billing_cadence)}`
              : "Legacy — se asignará un plan al emitir"}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-slate-500 mb-1">Módulos solicitados</dt>
          <dd>
            <RequestedModuleBadges modules={row.requested_modules} catalog={moduleCatalog} />
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Alcance usuarios / clientes</dt>
          <dd className="text-slate-800">
            {isExpansion && (row.current_authorized_users != null || row.requested_authorized_users != null)
              ? `${row.current_authorized_users ?? "—"} → ${row.requested_authorized_users ?? "—"} usuarios · ${row.current_authorized_clients ?? "—"} → ${row.requested_authorized_clients ?? "—"} clientes`
              : `${row.estimated_users ?? "—"} / ${row.estimated_clients ?? "—"}`}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Factura</dt>
          <dd className="text-slate-800">{formatRequiresInvoiceLabel(row.requires_invoice)}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-slate-500">Comprobante</dt>
          <dd className="text-slate-800">
            {row.payment_validation_status_label
              || paymentValidationStatusLabel(row.payment_validation_status)}
          </dd>
        </div>
      </dl>

      <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:flex-wrap">
        <Button type="button" size="sm" variant="outline" className="w-full sm:w-auto" onClick={() => onOpenDetail(row)}>
          <Eye className="w-3.5 h-3.5 mr-1" />
          Ver detalle
        </Button>
        {actions.map((action) => (
          <Button
            key={action.id}
            type="button"
            size="sm"
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => onOpenReview(row, action.status)}
          >
            {action.label}
          </Button>
        ))}
        {showFulfill ? (
          <Button
            type="button"
            size="sm"
            className="w-full sm:w-auto bg-[#0F2D5C] hover:bg-[#0a2247] text-white"
            onClick={() => onOpenFulfill(row)}
            data-testid={`fulfill-license-request-${row.id}`}
          >
            <Sparkles className="w-3.5 h-3.5 mr-1" />
            {isExpansion ? "Autorizar ampliación" : "Emitir licencia web"}
          </Button>
        ) : null}
        {showExpand ? (
          <Button
            type="button"
            size="sm"
            className="w-full sm:w-auto bg-[#0F2D5C] hover:bg-[#0a2247] text-white"
            onClick={() => onOpenExpand(row)}
            data-testid={`expand-issued-license-request-${row.id}`}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            Ampliar / renovar licencia
          </Button>
        ) : null}
      </div>
    </article>
  );
}

function statusBadgeClass(status) {
  switch (status) {
    case "pending":
      return "bg-amber-50 text-amber-900 border-amber-200";
    case "in_review":
      return "bg-sky-50 text-sky-900 border-sky-200";
    case "approved":
      return "bg-emerald-50 text-emerald-900 border-emerald-200";
    case "issued":
      return "bg-indigo-50 text-indigo-900 border-indigo-200";
    case "rejected":
      return "bg-red-50 text-red-900 border-red-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

function formatDateShort(iso) {
  if (!iso) return "—";
  return String(iso).slice(0, 10);
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const s = String(iso);
  return s.length >= 16 ? s.slice(0, 16).replace("T", " ") : s;
}

function displayName(row) {
  return licenseRequestDisplayName(row);
}

function RequestedModuleBadges({ modules = {}, catalog = DEFAULT_MODULE_CATALOG }) {
  const active = moduleIdsForSaasCenter(catalog).filter((id) => modules[id]);
  if (!active.length) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {active.map((id) => (
        <Badge
          key={id}
          variant="outline"
          className="text-[10px] font-normal border-[#0F2D5C]/20 text-[#0F2D5C]"
        >
          {moduleLabel(id, catalog)}
        </Badge>
      ))}
    </div>
  );
}

export default function PlatformLicenseRequests({ embedded = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [rfcFilter, setRfcFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [organizationIdFilter, setOrganizationIdFilter] = useState("");
  const [tenantBuyerIdFilter, setTenantBuyerIdFilter] = useState("");
  const [buyerCodeFilter, setBuyerCodeFilter] = useState("");
  const [orgCodeFilter, setOrgCodeFilter] = useState("");
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reviewTargetStatus, setReviewTargetStatus] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [patching, setPatching] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [fulfillOpen, setFulfillOpen] = useState(false);
  const [expandOpen, setExpandOpen] = useState(false);
  const [fulfillLoading, setFulfillLoading] = useState(false);
  const [fulfillSubmitting, setFulfillSubmitting] = useState(false);
  const [fulfillPreview, setFulfillPreview] = useState(null);
  const [plansCatalog, setPlansCatalog] = useState(null);
  const [fulfillCommercialPlan, setFulfillCommercialPlan] = useState("");
  const [fulfillBillingCadence, setFulfillBillingCadence] = useState("annual");
  const [fulfillAdvancedOverride, setFulfillAdvancedOverride] = useState(false);
  const [fulfillLicenseType, setFulfillLicenseType] = useState(DEFAULT_ISSUED_LICENSE_TYPE);
  const [fulfillNotes, setFulfillNotes] = useState("");
  const [fulfillAuthorizedModules, setFulfillAuthorizedModules] = useState({});
  const [fulfillAuthorizedUsers, setFulfillAuthorizedUsers] = useState(2);
  const [fulfillAuthorizedClients, setFulfillAuthorizedClients] = useState(25);
  const [paymentRejectReason, setPaymentRejectReason] = useState("");
  const [paymentValidating, setPaymentValidating] = useState(false);
  const [{ validFrom: fulfillValidFrom, validTo: fulfillValidTo }, setFulfillDates] = useState(
    defaultFulfillDates,
  );

  const [moduleCatalog, setModuleCatalog] = useState(DEFAULT_MODULE_CATALOG);
  const saasModuleIds = useMemo(() => moduleIdsForSaasCenter(moduleCatalog), [moduleCatalog]);
  const selectedFulfillPlan = useMemo(
    () => commercialPlanOption(plansCatalog, fulfillCommercialPlan),
    [plansCatalog, fulfillCommercialPlan],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      const status = platformLicenseRequestStatusQuery(statusFilter);
      if (status) filters.status = status;
      const rfc = rfcFilter.trim().toUpperCase();
      if (rfc) filters.rfc = rfc;
      const owner = ownerFilter.trim();
      if (owner) filters.owner_user_id = owner;
      const orgId = organizationIdFilter.trim();
      if (orgId) filters.organization_id = orgId;
      const tbid = tenantBuyerIdFilter.trim();
      if (tbid) filters.tenant_buyer_id = tbid;
      const bcode = buyerCodeFilter.trim();
      if (bcode) filters.buyer_code = bcode;
      const ocode = orgCodeFilter.trim();
      if (ocode) filters.org_code = ocode;
      setItems(await listPlatformLicenseRequests(filters));
    } catch (err) {
      toast.error(parsePlatformLicenseRequestListError(err));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, rfcFilter, ownerFilter, organizationIdFilter, tenantBuyerIdFilter, buyerCodeFilter, orgCodeFilter]);

  useEffect(() => {
    Promise.all([fetchPlatformModules(), fetchCommercialPlansCatalog()])
      .then(([modules, plans]) => {
        const items = modules?.items;
        if (Array.isArray(items) && items.length) setModuleCatalog(items);
        setPlansCatalog(plans);
      })
      .catch(() => {
        toast.error("No se pudo cargar la matriz de planes comerciales.");
      });
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const openDetail = async (row) => {
    setDetailOpen(true);
    setSelected(row);
    setDetailLoading(true);
    try {
      const full = await getPlatformLicenseRequest(row.id);
      setSelected(full);
    } catch (err) {
      toast.error(parsePlatformLicenseRequestPatchError(err));
      setDetailOpen(false);
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openReview = (row, targetStatus) => {
    setSelected(row);
    setReviewTargetStatus(targetStatus);
    setReviewNotes("");
    setReviewOpen(true);
  };

  const submitReview = async () => {
    if (!selected?.id || !reviewTargetStatus) return;
    setPatching(true);
    try {
      const updated = await updatePlatformLicenseRequestStatus(selected.id, {
        status: reviewTargetStatus,
        review_notes: reviewNotes.trim(),
      });
      toast.success(
        reviewTargetStatus === "approved"
          ? PLATFORM_LICENSE_REQUEST_MSG_APPROVED_NEXT
          : PLATFORM_LICENSE_REQUEST_MSG_PATCH_SUCCESS,
      );
      setReviewOpen(false);
      setSelected(updated);
      await reload();
      if (detailOpen) setSelected(updated);
    } catch (err) {
      toast.error(parsePlatformLicenseRequestPatchError(err));
    } finally {
      setPatching(false);
    }
  };

  const reviewActionLabel = useMemo(() => {
    const actions = platformLicenseRequestActions(selected?.status);
    return actions.find((a) => a.status === reviewTargetStatus)?.label || "Actualizar estado";
  }, [selected?.status, reviewTargetStatus]);

  const submitPaymentValidate = async () => {
    if (!selected?.id) return;
    setPaymentValidating(true);
    try {
      const updated = await validatePlatformPaymentProof(selected.id, { action: "validate" });
      toast.success("Comprobante marcado como validado.");
      setSelected(updated);
      setPaymentRejectReason("");
      await reload();
    } catch (err) {
      toast.error(parsePlatformLicenseRequestPatchError(err));
    } finally {
      setPaymentValidating(false);
    }
  };

  const submitPaymentReject = async () => {
    if (!selected?.id) return;
    const reason = paymentRejectReason.trim();
    if (!reason) {
      toast.error("Indique el motivo de rechazo del comprobante.");
      return;
    }
    setPaymentValidating(true);
    try {
      const updated = await validatePlatformPaymentProof(selected.id, {
        action: "reject",
        rejection_reason: reason,
      });
      toast.success("Comprobante rechazado.");
      setSelected(updated);
      setPaymentRejectReason("");
      await reload();
    } catch (err) {
      toast.error(parsePlatformLicenseRequestPatchError(err));
    } finally {
      setPaymentValidating(false);
    }
  };

  const openFulfill = async (row) => {
    setSelected(row);
    setFulfillOpen(true);
    setFulfillLoading(true);
    setFulfillPreview(null);
    setFulfillCommercialPlan("");
    setFulfillBillingCadence("annual");
    setFulfillAdvancedOverride(false);
    setFulfillLicenseType(DEFAULT_ISSUED_LICENSE_TYPE);
    setFulfillNotes("");
    setFulfillDates(defaultFulfillDates());
    try {
      const full = row?.requested_modules ? row : await getPlatformLicenseRequest(row.id);
      setSelected(full);
      const preview = await getPlatformLicenseRequestFulfillPreview(full.id);
      setFulfillPreview(preview);
      const planId = (
        full?.commercial_plan
        || preview?.commercial_plan
        || preview?.suggested_commercial_plan
        || "basic"
      );
      const planOption = commercialPlanOption(plansCatalog, planId);
      const mods = planOption?.entitlements || preview?.modules_to_emit || full?.requested_modules || {};
      setFulfillAuthorizedModules(
        Object.fromEntries(saasModuleIds.map((id) => [id, Boolean(mods[id])])),
      );
      setFulfillCommercialPlan(planId);
      setFulfillBillingCadence(
        full?.billing_cadence || preview?.billing_cadence || "annual",
      );
      const issuedDefault =
        preview?.default_issued_license_type
        || full?.requested_license_type
        || DEFAULT_ISSUED_LICENSE_TYPE;
      setFulfillLicenseType(issuedDefault);
      if (preview?.suggested_valid_from && preview?.suggested_valid_to) {
        setFulfillDates({
          validFrom: preview.suggested_valid_from,
          validTo: preview.suggested_valid_to,
        });
      }
      setFulfillAuthorizedUsers(
        Number(planOption?.limits?.authorized_users ?? preview?.default_authorized_users ?? full?.estimated_users ?? 2) || 2,
      );
      setFulfillAuthorizedClients(
        Number(planOption?.limits?.authorized_clients ?? preview?.default_authorized_clients ?? full?.estimated_clients ?? 25) || 25,
      );
    } catch (err) {
      toast.error(parsePlatformLicenseRequestFulfillError(err));
      setFulfillOpen(false);
    } finally {
      setFulfillLoading(false);
    }
  };

  const openExpand = (row) => {
    setSelected(row);
    setExpandOpen(true);
  };

  const submitFulfill = async () => {
    if (!selected?.id) return;
    if (!selectedFulfillPlan) {
      toast.error("Seleccione un plan comercial publicado antes de emitir.");
      return;
    }
    setFulfillSubmitting(true);
    try {
      const payload = {
        valid_from: fulfillValidFrom,
        valid_to: fulfillValidTo,
        commercial_plan: fulfillCommercialPlan,
        billing_cadence: fulfillBillingCadence,
        issued_license_type: fulfillLicenseType,
        review_notes: fulfillNotes.trim() || undefined,
        sync_distributor_catalog: true,
      };
      if (fulfillAdvancedOverride) {
        payload.override_plan_entitlements = true;
        payload.override_plan_limits = true;
        payload.authorized_modules = fulfillAuthorizedModules;
        payload.authorized_users = Number(fulfillAuthorizedUsers) || 1;
        payload.authorized_clients = Number(fulfillAuthorizedClients) || 1;
      }
      const result = await fulfillPlatformLicenseRequest(selected.id, payload);
      if (!isFulfillResultComplete(result)) {
        toast.error(PLATFORM_LICENSE_REQUEST_MSG_FULFILL_INCOMPLETE);
        return;
      }
      toast.success(PLATFORM_LICENSE_REQUEST_MSG_FULFILL_SUCCESS);
      setFulfillOpen(false);
      setSelected(result.request);
      await reload();
      if (detailOpen) setSelected(result.request);
    } catch (err) {
      toast.error(parsePlatformLicenseRequestFulfillError(err));
    } finally {
      setFulfillSubmitting(false);
    }
  };

  const onFulfillLicenseTypeChange = (nextType) => {
    setFulfillLicenseType(nextType);
    const suggested = suggestedValidityDates(nextType, fulfillValidFrom);
    setFulfillDates((d) => ({ ...d, validTo: suggested.validTo }));
  };

  const onFulfillCommercialPlanChange = (nextPlan) => {
    setFulfillCommercialPlan(nextPlan);
    const plan = commercialPlanOption(plansCatalog, nextPlan);
    if (!plan) return;
    setFulfillAuthorizedModules(
      Object.fromEntries(saasModuleIds.map((id) => [id, Boolean(plan.entitlements?.[id])])),
    );
    setFulfillAuthorizedUsers(Number(plan.limits?.authorized_users) || 1);
    setFulfillAuthorizedClients(Number(plan.limits?.authorized_clients) || 1);
  };

  const moduleAdjustmentWarning = useMemo(() => {
    const requested = selected?.requested_modules || {};
    const authIds = saasModuleIds.filter((id) => fulfillAuthorizedModules[id]);
    const reqIds = saasModuleIds.filter((id) => requested[id]);
    if (!reqIds.length) return "";
    if (authIds.length < reqIds.length) {
      return "Estás autorizando menos módulos que los solicitados por el comprador.";
    }
    if (authIds.length > reqIds.length) {
      return "Estás autorizando módulos adicionales a los solicitados.";
    }
    return "";
  }, [fulfillAuthorizedModules, saasModuleIds, selected?.requested_modules]);

  const scopeAdjustmentWarning = useMemo(() => {
    const reqU = Number(
      selected?.requested_authorized_users ?? selected?.estimated_users ?? 0,
    );
    const reqC = Number(
      selected?.requested_authorized_clients ?? selected?.estimated_clients ?? 0,
    );
    const authU = Number(fulfillAuthorizedUsers) || 0;
    const authC = Number(fulfillAuthorizedClients) || 0;
    const notes = [];
    if (reqU && authU < reqU) {
      notes.push("Estás autorizando un alcance menor al solicitado por el comprador (usuarios).");
    } else if (reqU && authU > reqU) {
      notes.push("Estás autorizando un alcance mayor al solicitado por el comprador (usuarios).");
    }
    if (reqC && authC < reqC) {
      notes.push("Estás autorizando un alcance menor al solicitado por el comprador (clientes).");
    } else if (reqC && authC > reqC) {
      notes.push("Estás autorizando un alcance mayor al solicitado por el comprador (clientes).");
    }
    return notes.join(" ");
  }, [fulfillAuthorizedUsers, fulfillAuthorizedClients, selected?.estimated_users, selected?.estimated_clients, selected?.requested_authorized_users, selected?.requested_authorized_clients]);

  const fulfillDialogTitle = selected && isExpansionRequestKind(selected.request_kind)
    ? "Autorizar ampliación / nueva revisión"
    : "Emitir licencia web desde solicitud";

  return (
    <div className="space-y-6" data-testid="platform-license-requests" data-embedded={embedded ? "true" : "false"}>
      {!embedded ? (
        <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm">
          <h2 className="text-base font-semibold text-[#0F2D5C]">Solicitudes de licencia</h2>
          <p className="text-sm text-slate-600 mt-2 max-w-3xl">
            Flujo diario: revisar solicitud → validar pago → aprobar → emitir licencia web.
          </p>
          <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-3 max-w-3xl">
            {PLATFORM_LICENSE_REQUEST_MSG_NO_AUTO_EMIT}
          </p>
        </section>
      ) : (
        <section className="rounded-sm border border-slate-200 bg-white p-5 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 m-0">Solicitudes formales de licencia</h3>
            <p className="text-xs text-slate-500 mt-1 mb-0 max-w-3xl">{PLATFORM_LICENSE_REQUEST_MSG_HEADER}</p>
          </div>
          <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 m-0 max-w-3xl">
            {PLATFORM_LICENSE_REQUEST_MSG_NO_AUTO_EMIT}
          </p>
        </section>
      )}

      <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm space-y-4">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map(({ id, label }) => (
            <Button
              key={id}
              type="button"
              size="sm"
              variant={statusFilter === id ? "default" : "outline"}
              className={statusFilter === id ? "bg-[#0F2D5C] hover:bg-[#0a2247]" : ""}
              onClick={() => setStatusFilter(id)}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="filter-rfc">RFC</Label>
            <Input
              id="filter-rfc"
              value={rfcFilter}
              onChange={(e) => setRfcFilter(e.target.value)}
              placeholder="Buscar por RFC del comprador"
              data-testid="platform-requests-filter-rfc"
            />
          </div>
          <div>
            <Label htmlFor="filter-buyer-code">Código comprador</Label>
            <Input
              id="filter-buyer-code"
              value={buyerCodeFilter}
              onChange={(e) => setBuyerCodeFilter(e.target.value)}
              placeholder="BNT-BUY-…"
              data-testid="platform-requests-filter-buyer-code"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs text-slate-600"
            onClick={() => setAdvancedFiltersOpen((v) => !v)}
            data-testid="platform-requests-advanced-filters-toggle"
          >
            {advancedFiltersOpen ? "Ocultar búsqueda avanzada" : "Búsqueda avanzada (IDs internos)"}
          </Button>
          {advancedFiltersOpen ? (
            <div
              className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 rounded-md border border-slate-200 bg-slate-50/60 p-3"
              data-testid="platform-requests-advanced-filters"
            >
              <div>
                <Label htmlFor="filter-org-id">organization_id</Label>
                <Input
                  id="filter-org-id"
                  value={organizationIdFilter}
                  onChange={(e) => setOrganizationIdFilter(e.target.value)}
                  placeholder="ID interno organización"
                />
              </div>
              <div>
                <Label htmlFor="filter-tenant-buyer">tenant_buyer_id</Label>
                <Input
                  id="filter-tenant-buyer"
                  value={tenantBuyerIdFilter}
                  onChange={(e) => setTenantBuyerIdFilter(e.target.value)}
                  placeholder="ID interno comprador"
                />
              </div>
              <div>
                <Label htmlFor="filter-org-code">org_code</Label>
                <Input
                  id="filter-org-code"
                  value={orgCodeFilter}
                  onChange={(e) => setOrgCodeFilter(e.target.value)}
                  placeholder="BNT-ORG-…"
                />
              </div>
              <div>
                <Label htmlFor="filter-owner">owner_user_id</Label>
                <Input
                  id="filter-owner"
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  placeholder="ID del titular / espacio"
                />
              </div>
            </div>
          ) : null}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={reload} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
          Aplicar filtros
        </Button>
      </section>

      <section className="rounded-lg border border-[#0F2D5C]/15 bg-white p-6 shadow-sm">
        {loading ? (
          <p className="text-sm text-slate-500">Cargando solicitudes…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500">No hay solicitudes con los filtros actuales.</p>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2" data-testid="platform-license-requests-cards">
            {items.map((row) => (
              <LicenseRequestCard
                key={row.id}
                row={row}
                moduleCatalog={moduleCatalog}
                onOpenDetail={openDetail}
                onOpenReview={openReview}
                onOpenFulfill={openFulfill}
                onOpenExpand={openExpand}
              />
            ))}
          </div>
        )}
      </section>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de solicitud</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <p className="text-sm text-slate-500">Cargando detalle…</p>
          ) : selected ? (
            <PlatformLicenseRequestDetailBody
              selected={selected}
              moduleCatalog={moduleCatalog}
              saasModuleIds={saasModuleIds}
              statusBadgeClass={statusBadgeClass}
              formatDateShort={formatDateShort}
              formatDateTime={formatDateTime}
              displayName={displayName}
              paymentRejectReason={paymentRejectReason}
              setPaymentRejectReason={setPaymentRejectReason}
              paymentValidating={paymentValidating}
              onPaymentValidate={submitPaymentValidate}
              onPaymentReject={submitPaymentReject}
              onOpenFulfill={() => {
                setDetailOpen(false);
                openFulfill(selected);
              }}
              onOpenExpand={() => {
                setDetailOpen(false);
                openExpand(selected);
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{reviewActionLabel}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              Estado destino:{" "}
              <strong>{licenseRequestStatusLabel(reviewTargetStatus)}</strong>
            </p>
            <div>
              <Label htmlFor="review-notes">Notas de revisión</Label>
              <SpellcheckTextarea
                id="review-notes"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder={PLATFORM_LICENSE_REQUEST_REVIEW_PLACEHOLDER}
                rows={4}
              />
              {reviewTargetStatus === "rejected" && (
                <p className="text-xs text-slate-500 mt-1">Recomendado indicar el motivo del rechazo.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReviewOpen(false)} disabled={patching}>
              Cancelar
            </Button>
            <Button
              type="button"
              className="bg-[#0F2D5C] hover:bg-[#0a2247]"
              disabled={patching}
              onClick={submitReview}
            >
              {patching ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fulfillOpen} onOpenChange={setFulfillOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{fulfillDialogTitle}</DialogTitle>
          </DialogHeader>
          {fulfillLoading ? (
            <p className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando vista previa…
            </p>
          ) : selected ? (
            <div className="space-y-4 text-sm">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs space-y-1">
                <p>
                  <span className="text-slate-500">Comprador:</span> {licenseRequestDisplayName(selected)} —{" "}
                  <span className="font-mono">{licenseRequestDisplayRfc(selected)}</span>
                </p>
                <p>
                  <span className="text-slate-500">Correo:</span> {selected.email || "—"}
                </p>
                <p>
                  <span className="text-slate-500">Factura requerida:</span>{" "}
                  {formatRequiresInvoiceLabel(selected.requires_invoice)}
                </p>
                {selected.requires_invoice ? (
                  <>
                    <p className="text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1">
                      Esta solicitud requiere factura. Verifica los datos fiscales para realizarla manualmente en el
                      sistema externo correspondiente.
                    </p>
                    <div className="text-[11px] text-slate-700 space-y-0.5 mt-1">
                      <p>
                        <span className="text-slate-500">RFC:</span>{" "}
                        <span className="font-mono">{selected.invoice_rfc || "—"}</span>
                      </p>
                      <p>
                        <span className="text-slate-500">Razón social:</span>{" "}
                        {selected.invoice_razon_social || "—"}
                      </p>
                      <p>
                        <span className="text-slate-500">Régimen fiscal:</span>{" "}
                        {selected.invoice_regimen_fiscal || "—"}
                      </p>
                      <p>
                        <span className="text-slate-500">C.P. fiscal:</span>{" "}
                        {selected.invoice_codigo_postal || "—"}
                      </p>
                      <p>
                        <span className="text-slate-500">Domicilio fiscal:</span>{" "}
                        {selected.invoice_fiscal_address || "No especificado"}
                      </p>
                      <p>
                        <span className="text-slate-500">Correo facturación:</span>{" "}
                        {selected.invoice_email || "—"}
                      </p>
                      <p>
                        <span className="text-slate-500">Uso CFDI:</span>{" "}
                        {selected.invoice_cfdi_use_label
                          || cfdiUseSelectLabel(selected.invoice_cfdi_use)}
                      </p>
                      <p>
                        <span className="text-slate-500">Forma de pago:</span>{" "}
                        {selected.invoice_payment_method_label
                          || paymentMethodSelectLabel(selected.invoice_payment_method)}
                      </p>
                    </div>
                  </>
                ) : null}
                {isPaymentProofPending(selected) ? (
                  <p
                    className="text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1"
                    data-testid="fulfill-payment-pending-warning"
                  >
                    {PLATFORM_LICENSE_REQUEST_MSG_PAYMENT_PENDING}
                  </p>
                ) : isPaymentProofRejected(selected) ? (
                  <p
                    className="text-rose-900 bg-rose-50 border border-rose-200 rounded px-2 py-1.5 mt-1"
                    data-testid="fulfill-payment-rejected-warning"
                  >
                    {PLATFORM_LICENSE_REQUEST_MSG_PAYMENT_REJECTED}
                  </p>
                ) : selected.payment_validation_status === "validated" ? (
                  <p className="text-emerald-800 text-[11px] mt-1">Pago validado por BNT.</p>
                ) : null}
                {fulfillPreview?.installation_id ? (
                  <p>
                    <span className="text-slate-500">Instalación:</span>{" "}
                    <span className="font-mono">{fulfillPreview.installation_id}</span>
                  </p>
                ) : null}
                {fulfillPreview?.organization_existing ? (
                  <p className="text-amber-800">
                    Se enlazará organización existente ({fulfillPreview.organization_existing.name}).
                  </p>
                ) : (
                  <p className="text-emerald-800">Se creará organización nueva con el RFC de la solicitud.</p>
                )}
                {fulfillPreview?.buyer_existing_id ? (
                  <p className="text-amber-800">Se actualizará comprador existente en catálogo.</p>
                ) : (
                  <p className="text-emerald-800">Se dará de alta comprador en catálogo.</p>
                )}
              </div>
              <div>
                <Label htmlFor="fulfill-commercial-plan">Plan comercial</Label>
                <Select value={fulfillCommercialPlan} onValueChange={onFulfillCommercialPlanChange}>
                  <SelectTrigger id="fulfill-commercial-plan" className="mt-1" data-testid="fulfill-commercial-plan-select">
                    <SelectValue placeholder="Seleccione un plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {["basic", "professional"].map((planId) => {
                      const plan = commercialPlanOption(plansCatalog, planId);
                      return plan ? (
                        <SelectItem key={planId} value={planId}>
                          {plan.label}
                        </SelectItem>
                      ) : null;
                    })}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">
                  {selected?.commercial_plan
                    ? `Solicitado por el comprador: ${commercialPlanLabel(selected.commercial_plan)}.`
                    : "Solicitud legacy: seleccione el plan que corresponde antes de emitir."}
                </p>
                {selectedFulfillPlan ? (
                  <div className="mt-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-[11px] text-slate-700">
                    Incluye: {commercialPlanModuleIds(selectedFulfillPlan).map((id) => moduleLabel(id, moduleCatalog)).join(" · ")}
                    {" · "}Límite: {selectedFulfillPlan.limits?.authorized_users ?? "—"} usuarios / {selectedFulfillPlan.limits?.authorized_clients ?? "—"} clientes.
                  </div>
                ) : null}
              </div>
              <div>
                <Label className="text-xs text-slate-600">Cadencia de cobro</Label>
                <Select value={fulfillBillingCadence} onValueChange={setFulfillBillingCadence}>
                  <SelectTrigger className="mt-1 h-9 text-xs" data-testid="fulfill-billing-cadence-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly" className="text-xs">Mensual</SelectItem>
                    <SelectItem value="annual" className="text-xs">Anual</SelectItem>
                  </SelectContent>
                </Select>
                <label className="mt-3 flex items-start gap-2 text-[11px] text-slate-700 cursor-pointer">
                  <Checkbox
                    checked={fulfillAdvancedOverride}
                    onCheckedChange={(value) => setFulfillAdvancedOverride(value === true)}
                    data-testid="fulfill-advanced-plan-override"
                  />
                  <span>
                    Ajustar módulos y límites manualmente (soporte excepcional). Si no se activa, se aplica exactamente la matriz del plan.
                  </span>
                </label>
              </div>
              {fulfillAdvancedOverride ? (
                <>
                  <div>
                    <Label className="text-xs text-slate-600">Módulos a autorizar (ajuste avanzado)</Label>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Solicitados: {formatRequestedModulesLabel(selected?.requested_modules, moduleCatalog) || "—"}
                    </p>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {saasModuleIds.map((moduleId) => (
                        <label
                          key={moduleId}
                          className="flex items-start gap-2 rounded-sm border border-slate-200 bg-white px-3 py-2 text-[11px] cursor-pointer"
                        >
                          <Checkbox
                            checked={Boolean(fulfillAuthorizedModules[moduleId])}
                            onCheckedChange={(v) =>
                              setFulfillAuthorizedModules((prev) => ({
                                ...prev,
                                [moduleId]: v === true,
                              }))
                            }
                            data-testid={`fulfill-module-${moduleId}`}
                          />
                          <span>{moduleLabel(moduleId, moduleCatalog)}</span>
                        </label>
                      ))}
                    </div>
                    {moduleAdjustmentWarning ? (
                      <p
                        className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2"
                        data-testid="fulfill-module-adjustment-warning"
                      >
                        {moduleAdjustmentWarning}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Topes autorizados (ajuste avanzado)</Label>
                    {fulfillPreview?.current_authorized_users != null || fulfillPreview?.requested_authorized_users != null ? (
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        Actual: {fulfillPreview?.current_authorized_users ?? "—"} usuarios ·{" "}
                        {fulfillPreview?.current_authorized_clients ?? "—"} clientes — Solicitado:{" "}
                        {fulfillPreview?.requested_authorized_users ?? selected?.requested_authorized_users ?? "—"} usuarios ·{" "}
                        {fulfillPreview?.requested_authorized_clients ?? selected?.requested_authorized_clients ?? "—"} clientes
                      </p>
                    ) : null}
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="fulfill-auth-users" className="text-xs">Usuarios autorizados</Label>
                        <Input
                          id="fulfill-auth-users"
                          type="number"
                          min={1}
                          value={fulfillAuthorizedUsers}
                          onChange={(e) => setFulfillAuthorizedUsers(e.target.value)}
                          className="mt-1 h-9 text-xs"
                          data-testid="fulfill-authorized-users"
                        />
                      </div>
                      <div>
                        <Label htmlFor="fulfill-auth-clients" className="text-xs">Clientes autorizados</Label>
                        <Input
                          id="fulfill-auth-clients"
                          type="number"
                          min={1}
                          value={fulfillAuthorizedClients}
                          onChange={(e) => setFulfillAuthorizedClients(e.target.value)}
                          className="mt-1 h-9 text-xs"
                          data-testid="fulfill-authorized-clients"
                        />
                      </div>
                    </div>
                    {selected?.special_quote_required ? (
                      <p className="text-[11px] text-violet-900 bg-violet-50 border border-violet-200 rounded px-2 py-1.5 mt-2">
                        Cotización especial: el comprador solicitó más de 99 clientes. Puede autorizar el número acordado.
                      </p>
                    ) : null}
                    {scopeAdjustmentWarning ? (
                      <p
                        className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2"
                        data-testid="fulfill-scope-adjustment-warning"
                      >
                        {scopeAdjustmentWarning}
                      </p>
                    ) : null}
                    <p className="text-[10px] text-slate-500 mt-2">{LICENSE_SCOPE_COST_DISCLAIMER}</p>
                  </div>
                </>
              ) : null}
              <div>
                <Label htmlFor="fulfill-license-type">Tipo de licencia a emitir</Label>
                <Select value={fulfillLicenseType} onValueChange={onFulfillLicenseTypeChange}>
                  <SelectTrigger id="fulfill-license-type" className="mt-1" data-testid="fulfill-license-type-select">
                    <SelectValue placeholder="Tipo de licencia" />
                  </SelectTrigger>
                  <SelectContent>
                    {LICENSE_TYPE_IDS.map((id) => (
                      <SelectItem key={id} value={id}>
                        {licenseTypeLabel(id)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selected?.requested_license_type ? (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Solicitado: {licenseTypeLabel(selected.requested_license_type)}. Puede cambiarlo antes de confirmar.
                  </p>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="fulfill-from">Vigencia desde</Label>
                  <Input
                    id="fulfill-from"
                    type="date"
                    value={fulfillValidFrom}
                    onChange={(e) => setFulfillDates((d) => ({ ...d, validFrom: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="fulfill-to">Vigencia hasta</Label>
                  <Input
                    id="fulfill-to"
                    type="date"
                    value={fulfillValidTo}
                    onChange={(e) => setFulfillDates((d) => ({ ...d, validTo: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="fulfill-notes">Notas de emisión (opcional)</Label>
                <SpellcheckTextarea
                  id="fulfill-notes"
                  value={fulfillNotes}
                  onChange={(e) => setFulfillNotes(e.target.value)}
                  rows={3}
                  placeholder="Comentario interno BNT"
                />
              </div>
              <p className="text-xs text-slate-500">
                La licencia quedará activa en la cuenta web del comprador sin pegar token manual.
              </p>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFulfillOpen(false)} disabled={fulfillSubmitting}>
              Cancelar
            </Button>
            <Button
              type="button"
              className="bg-[#0F2D5C] hover:bg-[#0a2247]"
              disabled={
                fulfillSubmitting
                || fulfillLoading
                || !selected
                || isPaymentProofPending(selected)
                || isPaymentProofRejected(selected)
                || fulfillPreview?.payment_blocked
                || !selectedFulfillPlan
              }
              onClick={submitFulfill}
              data-testid="confirm-fulfill-license-request"
            >
              {fulfillSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Confirmar emisión web
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PlatformIssuedLicenseExpandDialog
        open={expandOpen}
        onOpenChange={setExpandOpen}
        requestRow={selected}
        plansCatalog={plansCatalog}
        onDone={() => {
          reload();
        }}
      />
    </div>
  );
}
